import { Lender } from '../firebase/fetchLenders';
import { State } from '../firebase/fetchStates';

/**
 * Check if a lender has a specific term duration
 * @param {Object} lender - The lender object
 * @param {number} term - The term duration to check (e.g., 24, 36, 60)
 * @returns {boolean} True if the lender has that term duration
 */
export function hasTerm(lender: Lender, term: number): boolean {
  return lender.termConfigurations.some((t) => t.isActive && t.term === term);
}

/**
 * Get the minimum loan required for a specific term
 * @param {Object} lender - The lender object
 * @param {number} term - The term duration (e.g., 24, 36, 60)
 * @returns {number} The minimum loan required for that term, or 0 if not found
 */
export function getMinLoanRequired(lender: Lender, term: number): number {
  const lenderTerm = lender.termConfigurations.find((t) => t.term === term);
  return lenderTerm?.minLoanRequired || 0;
}

/**
 * Get all available states for a specific lender
 * @param {Array} states - Array of all state objects from Firebase
 * @param {Object} lender - The selected lender object
 * @param {number} activeTerm - The active finance plan term duration (default: 36)
 * @returns {Array} Array of states where this lender is available
 */
export function getStatesForLender(
  states: State[],
  lender: Lender,
  activeTerm: number = 36
): State[] {
  const lenderDisabledStates = lender.disabledStates || [];
  const termDisabledStates =
    lender.termConfigurations.find((t) => t.term === activeTerm)
      ?.disabledStates || [];
  const allDisabledStates = Array.from(
    new Set([...lenderDisabledStates, ...termDisabledStates])
  );
  return states.filter((state) => !allDisabledStates.includes(state.id));
}

/**
 * Get all available lenders for a specific state
 * @param {string} stateId - The state document ID (e.g., "arizona", "california")
 * @param {Array} lenders - Array of all lender objects from Firebase
 * @param {number} activeTerm - The active finance plan term duration (default: 36)
 * @returns {Array} Array of lenders available in this state, each with their terms filtered
 */
export function getLendersForState(
  stateId: string,
  lenders: Lender[],
  activeTerm: number = 36
): Lender[] {
  return lenders
    .filter((lender) => {
      // Check if lender has termConfigurations
      if (
        !lender.termConfigurations ||
        lender.termConfigurations.length === 0
      ) {
        return false;
      }

      // Check if activeTerm exists and is active in termConfigurations
      const activeTermConfig = lender.termConfigurations.find(
        (tc) => tc.term === activeTerm
      );
      if (!activeTermConfig || !activeTermConfig.isActive) {
        return false;
      }

      // Check if lender is disabled in this state
      const lenderDisabledStates = lender.disabledStates || [];
      const termDisabledStates = activeTermConfig.disabledStates || [];
      const allDisabledStates = Array.from(
        new Set([...lenderDisabledStates, ...termDisabledStates])
      );
      return !allDisabledStates.includes(stateId);
    })
    .map((lender) => {
      // Filter termConfigurations to exclude those where stateId is in disabledStates
      const filteredTermConfigurations = lender.termConfigurations.filter(
        (tc) => {
          const termDisabledStates = tc.disabledStates || [];
          return !termDisabledStates.includes(stateId);
        }
      );

      // For each lender, filter terms to only include those relevant to this state
      const filteredTerms: any[] = [];

      // Group terms by duration
      const termsByDuration: { [key: string]: any[] } = {};
      (lender.terms || []).forEach((term: any) => {
        if (!termsByDuration[term.term]) {
          termsByDuration[term.term] = [];
        }
        termsByDuration[term.term].push(term);
      });

      // For each term duration, decide which variant to include
      Object.entries(termsByDuration).forEach(([duration, terms]) => {
        const termDuration = parseInt(duration);

        // Check if this term duration is active in termConfigurations
        const termConfig = lender.termConfigurations.find(
          (tc) => tc.term === termDuration
        );

        // Skip this term duration if it's not active or if stateId is in its disabledStates
        if (!termConfig || !termConfig.isActive) {
          return;
        }

        const termDisabledStates = termConfig.disabledStates || [];
        if (termDisabledStates.includes(stateId)) {
          return;
        }

        // First, check if there's a state-specific variant for this state
        const stateSpecificVariant = terms.find(
          (t) => !t.isDefault && t.assignedStates?.includes(stateId)
        );

        if (stateSpecificVariant) {
          // This state has a specific variant
          filteredTerms.push(stateSpecificVariant);
        } else {
          // Check if this state is assigned to ANY other variant
          const isStateAssignedToAnotherVariant = terms.some(
            (t) => !t.isDefault && t.assignedStates?.includes(stateId)
          );

          // Only include default if state is NOT assigned to another variant
          if (!isStateAssignedToAnotherVariant) {
            const defaultTerm = terms.find((t) => t.isDefault);
            if (defaultTerm) {
              filteredTerms.push(defaultTerm);
            }
          }
        }
      });

      return {
        ...lender,
        terms: filteredTerms,
        termConfigurations: filteredTermConfigurations,
      };
    });
}
